import { FC, ReactNode, useRef, useState } from 'react';
import { classes, media, stylesheet } from 'typestyle';
import {
  Gray20,
  Gray100,
  typeCompact12MediumProperties,
  typeCompact12RegularProperties,
  typeCompact14MediumProperties,
  typeCompact14RegularProperties,
  Gray150,
  BlackOpacity04,
  Gray10,
  TextInput,
  Tooltip,
} from '@traceair/tds';
import { DeliverableStatusBadge } from './DeliverableStatusBadge';
import { TreeConnector } from './TreeConnector';
import { InvoicingCostItem } from '../types';
import { getItemRateInfo, numericOnly } from '../InvoicingUtils';
import { sharedInvoicingCss } from '../styles/shared';
import { BREAKPOINT_SMALL } from '../../../layout/styles';

const MOBILE = { maxWidth: BREAKPOINT_SMALL };

function getCategorySummary(category: string | undefined, itemCount: number): string | undefined {
  const n = itemCount;
  switch (category) {
    case 'scan':
      return n === 1 ? 'Scan' : `${n} Scans`;
    case 'pano':
      return `${n} Panorama${n !== 1 ? 's' : ''}`;
    case 'lidar':
      return n === 1 ? 'LiDAR' : `${n} LiDAR`;
    case 'gcp_services':
      return n === 1 ? 'GCP service' : `${n} GCP services`;
    case 'video':
      return n === 1 ? 'Video' : `${n} Videos`;
    case 'additional-images':
      return n === 1 ? 'Image' : `${n} Images`;
    case 'other':
      return n === 1 ? '1 other' : `${n} others`;
    default:
      return undefined;
  }
}

// Site/customer text truncates with an ellipsis when it doesn't fit — this only opens the
// tooltip once the text is actually cut off, showing the full text on hover. Used by the T&M
// nested row's combined "site · customer" text.
const TooltipTruncatedText: FC<{ text: string; className: string }> = ({ text, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <Tooltip title={text} open={open}>
      <div
        ref={ref}
        className={className}
        onMouseEnter={() => {
          if (ref.current && ref.current.scrollWidth > ref.current.clientWidth) setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        {text}
      </div>
    </Tooltip>
  );
};

// The fixed-fee row's site name and customer render as two stacked lines, each independently
// truncatable — this wraps both in a single shared tooltip (rather than one per line, which
// would show two overlapping tooltips as the pointer crosses from one line to the other) and
// opens it if either line is actually cut off, showing the full "site · customer" text.
const TooltipTruncatedSiteAccount: FC<{ siteName: string; customer: string }> = ({ siteName, customer }) => {
  const siteRef = useRef<HTMLParagraphElement>(null);
  const accountRef = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const isTruncated = (el: HTMLElement | null) => !!el && el.scrollWidth > el.clientWidth;
  return (
    <Tooltip title={customer ? `${siteName} · ${customer}` : siteName} open={open}>
      <div
        onMouseEnter={() => {
          if (isTruncated(siteRef.current) || isTruncated(accountRef.current)) setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        <p ref={siteRef} className={st.site}>
          {siteName}
        </p>
        <p ref={accountRef} className={st.account}>
          {customer}
        </p>
      </div>
    </Tooltip>
  );
};

const st = stylesheet({
  row: {
    display: 'grid',
    gap: 4,
    padding: '12px 24px 12px 20px',
    borderBottom: `1px solid ${Gray10}`,
    $nest: {
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    alignItems: 'center',
    ...media(MOBILE, {
      // minmax(0, 1fr), not a bare 1fr — a bare `fr` track's implicit minimum is its content's
      // min-content size (the site text's full unwrapped width), which stops it from ever
      // shrinking enough to trigger the site name/customer's ellipsis truncation.
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      gridTemplateRows: 'auto auto',
    }),
  },
  rowGcpEditable: {
    padding: '12px 16px 12px 20px',
  },
  // Lays the tree connector and `rowNestedContent` out side by side — no gap, so the connector's
  // own 56px width is the row's entire left gutter (matching the 56px left padding on the
  // category header above it) with no extra offset from the content that follows.
  rowNested: {
    display: 'flex',
    backgroundColor: BlackOpacity04,
    height: 40,
    gap: 4,
  },
  // Holds `siteGroupNested` + `rightBlockNested` and owns the padding and border-bottom. Fills
  // `rowNested`'s full height (default flex `stretch`), so its border-bottom lands exactly on
  // the row's true bottom edge regardless of top/bottom padding.
  rowNestedContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'space-between',
    minWidth: 0,
    padding: '10px 24px 10px 0',
    borderBottom: `1px solid ${Gray20}`,
  },
  rowNestedGcpEditable: {
    paddingRight: '16px !important',
  },
  failedRow: {
    $nest: {
      '& p, & div': {
        color: Gray100,
      },
    },
  },
  date: {
    ...typeCompact12RegularProperties,
    margin: 0,
    color: Gray150,
    ...media(MOBILE, {
      gridColumn: '1',
      gridRow: '1 / span 2',
      alignSelf: 'start',
      paddingTop: 2,
    }),
  },
  siteGroup: {
    minWidth: 0,
    ...media(MOBILE, { gridColumn: '2', gridRow: '1' }),
  },
  // Used whenever the row has an action button to make room for (regardless of whether it's
  // currently visible — it's kept mounted and just `display: none`-hidden, see
  // `actionNodeHidden`), so its layout doesn't shift between hovered and idle states.
  siteGroupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    maxWidth: 279,
    paddingRight: 8,
    ...media(MOBILE, { gridColumn: '2', gridRow: '1' }),
  },
  siteContent: {
    flex: 1,
    minWidth: 0,
  },
  siteGroupNested: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    width: 422,
    justifyContent: 'space-between',
  },
  // No fixed width — the action button renders at its natural size (only while shown), and
  // `rowNestedText`'s `flex: 1, minWidth: 0` shrinks/truncates to make room for it.
  actionCellNested: {
    flexShrink: 0,
  },
  // Hides the action button via `display: none` (zero layout space, unlike opacity) while
  // keeping it mounted — unmounting it instead would tear down its open dropdown/menu (and any
  // state inside it) the instant the pointer leaves the row en route to a portaled menu item.
  actionNodeHidden: {
    display: 'none',
  },
  site: {
    ...typeCompact14RegularProperties,
    margin: 0,
    color: Gray150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  // Fills the row's full width by default; only shrinks (truncating further) once the GCP
  // action button is shown alongside it and competes for space.
  rowNestedText: {
    ...typeCompact12RegularProperties,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  account: {
    ...typeCompact12RegularProperties,
    margin: '2px 0 0 0',
    color: Gray100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rightBlock: {
    ...typeCompact14RegularProperties,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifySelf: 'end',
    ...media(MOBILE, { gridColumn: '2', gridRow: '2', marginTop: 2 }),
  },
  badgeAmountGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  rightBlockNested: {
    ...typeCompact12RegularProperties,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    $nest: {
      '.ta-text-input div': {
        top: 10,
      },
    },
  },
  rate: {
    color: Gray150,
    flex: 1,
    textAlign: 'right' as const,
  },
  // Fixed width (instead of hugging the rate text) so the amount input's width doesn't shift
  // the action cell's position when the rate string's length varies row to row.
  rateGcpFixed: {
    width: 170,
    textAlign: 'right' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  badge: {
    flexShrink: 0,
  },
  amount: {
    ...typeCompact14MediumProperties,
    color: Gray150,
    flexShrink: 0,
    ...media(MOBILE, { gridColumn: '3', gridRow: '1', alignSelf: 'start', paddingTop: 2, marginLeft: 0 }),
  },
  amountFailed: {
    textDecorationLine: 'line-through',
  },
  compactAmountInputContainer: {
    $nest: {
      '& .ta-text-input': {
        minHeight: 32,
      },
      '& input': {
        height: 32,
        ...typeCompact12MediumProperties,
      },
    },
  },
});

type ItemRowProps = {
  item: InvoicingCostItem;
  hideDate?: boolean;
  // When set, the amount is rendered as an editable input (pre-filled from `amountInput`)
  // instead of the static `item.amount` display — used to let a pilot override a single
  // GCP service item's calculated price while submitting an invoice.
  amountInput?: string;
  onAmountChange?: (val: string) => void;
  // Extra action rendered alongside the site name — used for the "add GCP Materials/Mileage
  // extra cost" button on a GCP service item while submitting an invoice.
  actionNode?: ReactNode;
  // Only meaningful alongside `hideDate` — draws the tree connector line showing this row as a
  // child of its T&M date group's category header (e.g. "Scans, panoramas price"), terminating
  // the trunk line instead of continuing it when this is the last sibling in the list.
  isLast?: boolean;
};

export const ItemRow: FC<ItemRowProps> = ({ item, hideDate, amountInput, onAmountChange, actionNode, isLast }) => {
  const isFailed = item.status === 'failed';
  const isOnReview = item.status === 'on_review';
  const rateInfo = getItemRateInfo(item);
  const [isRowHovered, setIsRowHovered] = useState(false);
  const rowHoverHandlers = {
    onMouseEnter: () => setIsRowHovered(true),
    onMouseLeave: () => setIsRowHovered(false),
    onFocus: () => setIsRowHovered(true),
    onBlur: () => setIsRowHovered(false),
  };
  // The action button (e.g. "+ Costs") is only visible while the row is hovered/focused, hidden
  // via `display: none` (not opacity) the rest of the time so it reserves no layout space and
  // site details can use the row's full width — but it stays mounted throughout, so an open
  // dropdown/menu isn't torn down mid-click just because the pointer left the row en route to it.
  const showActionNode = isRowHovered && !!actionNode;

  const isGcpRow = item.category === 'gcp_services';
  const isGcpEditable = isGcpRow && amountInput !== undefined;

  const badgeAndAmount = (
    <>
      {isFailed && <DeliverableStatusBadge className={st.badge} label="Failed" />}
      {isOnReview && <DeliverableStatusBadge className={st.badge} label="In review" />}
      {amountInput !== undefined ? (
        <div
          className={classes(sharedInvoicingCss.priceInputContainer, hideDate && st.compactAmountInputContainer)}
          onClick={(e) => e.stopPropagation()}
        >
          <TextInput
            leftCell={amountInput ? '$' : undefined}
            value={amountInput}
            placeholder="Price"
            onChange={(val) => onAmountChange?.(numericOnly(val))}
          />
        </div>
      ) : (
        !!item.amount && (
          <div className={classes(st.amount, isFailed && st.amountFailed)}>${item.amount.toLocaleString()}</div>
        )
      )}
    </>
  );

  if (hideDate) {
    const displaySummary = getCategorySummary(item.category, item.itemCount ?? 1) ?? rateInfo;
    return (
      <div className={classes(st.rowNested, isFailed && st.failedRow)} {...rowHoverHandlers}>
        <TreeConnector isLast={!!isLast} />
        <div className={classes(st.rowNestedContent, isGcpEditable && st.rowNestedGcpEditable)}>
          <div className={st.siteGroupNested}>
            <TooltipTruncatedText
              text={item.customer ? `${item.siteName} · ${item.customer}` : item.siteName}
              className={st.rowNestedText}
            />
            {actionNode && (
              <div className={classes(st.actionCellNested, !showActionNode && st.actionNodeHidden)}>{actionNode}</div>
            )}
          </div>

          <div className={st.rightBlockNested}>
            {!isGcpRow && displaySummary && <div className={st.rate}>{displaySummary}</div>}
            {isGcpRow && rateInfo && <div className={classes(st.rate, st.rateGcpFixed)}>{rateInfo}</div>}
            {badgeAndAmount}
          </div>
        </div>
      </div>
    );
  }

  // minmax(0, 1fr), not a bare 1fr — a bare `fr` track's implicit minimum is its content's
  // min-content size (the site text's full unwrapped width), which stops it from ever shrinking
  // enough to trigger the site name/customer's ellipsis truncation.
  const gridTemplateColumns = rateInfo ? '120px minmax(0px, 1fr) 221px' : '120px 307px 1fr';
  return (
    <div
      className={classes(st.row, isGcpEditable && st.rowGcpEditable, isFailed && st.failedRow)}
      style={{ gridTemplateColumns }}
      {...rowHoverHandlers}
    >
      <div className={st.date}>{item.date}</div>
      <div className={actionNode ? st.siteGroupRow : st.siteGroup}>
        <div className={actionNode ? st.siteContent : undefined}>
          <TooltipTruncatedSiteAccount siteName={item.siteName} customer={item.customer} />
        </div>
        {actionNode && <div className={!showActionNode ? st.actionNodeHidden : undefined}>{actionNode}</div>}
      </div>
      <div className={st.rightBlock}>
        {rateInfo && <div className={st.rate}>{rateInfo}</div>}
        <div className={st.badgeAmountGroup}>{badgeAndAmount}</div>
      </div>
    </div>
  );
};
